/**
 * React Query hooks for Dashboard
 */

import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../services/dashboard.service";
import type {
  SalesmanAnalysis,
  SalesmanWiseMarginAnalysis,
  DashboardOnlineItem,
  DashboardExpiresResponse,
  DashboardOutstandingResponse,
  DashboardSalesPurchasesItem,
  DashboardSummaryResponse,
  DeadStockItem,
  MarginAnalysis,
  ManufacturerMargin,
  CustomerMargin,
  MarginSummaryValues,
  MarginTrendMonthlyPerformance,
  CreditAgingVsMargin,
  RenewDetailsResponse,
  PlanDetails,
} from "../types";

export const useDashboardData = (params?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["dashboard", params],
    queryFn: () => dashboardService.getDashboardData(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDashboardMetrics = (params?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["dashboard", "metrics", params],
    queryFn: () => dashboardService.getMetrics(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useSalesData = (params?: {
  startDate?: string;
  endDate?: string;
  period?: "monthly" | "weekly" | "daily";
}) => {
  return useQuery({
    queryKey: ["dashboard", "sales", params],
    queryFn: () => dashboardService.getSalesData(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useAgingData = () => {
  return useQuery({
    queryKey: ["dashboard", "aging"],
    queryFn: () => dashboardService.getAgingData(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useSalesmanAnalysis = (params?: { firmId?: number }) => {
  return useQuery<SalesmanAnalysis[]>({
    queryKey: ["dashboard", "salesman", params],
    queryFn: () => dashboardService.getSalesmanAnalysis(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useSalesmanWiseMarginAnalysis = (params?: { firmId?: number }) => {
  return useQuery<SalesmanWiseMarginAnalysis[]>({
    queryKey: ["dashboard", "salesman-wise-margin-analysis", params],
    queryFn: () => dashboardService.getSalesmanWiseMarginAnalysis(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useOnlineDashboard = (params?: { firmId?: number }) => {
  return useQuery<DashboardOnlineItem[]>({
    queryKey: ["dashboard", "online", params],
    queryFn: () => dashboardService.getOnlineDashboard(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDashboardExpires = (params?: { firmId?: number }) => {
  return useQuery<DashboardExpiresResponse>({
    queryKey: ["dashboard", "expires", params],
    queryFn: () => dashboardService.getExpires(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDashboardOutstanding = (params?: { firmId?: number }) => {
  return useQuery<DashboardOutstandingResponse>({
    queryKey: ["dashboard", "outstanding", params],
    queryFn: () => dashboardService.getOutstandingDashboard(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRenewDetails = (params?: { firmId?: number }) => {
  return useQuery<RenewDetailsResponse>({
    queryKey: ["dashboard", "renew-details", params],
    queryFn: () => dashboardService.getRenewDetails(params),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(params?.firmId),
  });
};

export const useDashboardSalesPurchases = (params?: { firmId?: number; targetPercentage?: number }) => {
  return useQuery<DashboardSalesPurchasesItem[]>({
    queryKey: ["dashboard", "sales-purchases", params],
    queryFn: () => dashboardService.getSalesPurchases(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData ?? [],
  });
};

export const useDashboardSalesPurchasesWithoutTarget = (params?: { firmId?: number }) => {
  return useQuery<DashboardSalesPurchasesItem[]>({
    queryKey: ["dashboard", "sales-purchases-without-target", params],
    queryFn: () => dashboardService.getSalesPurchasesWithoutTarget(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData ?? [],
  });
};

export const useDashboardSummary = (params?: { firmId?: number }) => {
  return useQuery<DashboardSummaryResponse>({
    queryKey: ["dashboard", "summary", params],
    queryFn: () => dashboardService.getDashboardSummary(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDeadStocks = (params?: { firmId?: number }) => {
  return useQuery<DeadStockItem[]>({
    queryKey: ["dashboard", "dead-stocks", params],
    queryFn: () => dashboardService.getDeadStocks(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData ?? [],
  });
};

export const useMarginAnalysis = (params?: { firmId?: number }) => {
  return useQuery<MarginAnalysis[]>({
    queryKey: ["dashboard", "margin-analysis", params],
    queryFn: async () => {
      try {
        return []; //await dashboardService.getMarginAnalysis(params);
      } catch (error: any) {
        // Handle 404 errors gracefully - endpoint might not exist yet
        if (error?.status === 404) {
          console.warn("Margin analysis endpoint not found (404), returning empty array");
          return [];
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.status === 404) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    placeholderData: (previousData) => previousData ?? [],
  });
};

export const useManufacturerMargins = (params?: { firmId?: number }) => {
  return useQuery<ManufacturerMargin[]>({
    queryKey: ["dashboard", "manufacturer-margins", params],
    queryFn: () => dashboardService.getManufacturerMargins(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCustomerMargins = (params?: { firmId?: number }) => {
  return useQuery<CustomerMargin[]>({
    queryKey: ["dashboard", "customer-margins", params],
    queryFn: () => dashboardService.getCustomerMargins(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMarginSummaryValues = (params?: { firmId?: number }) => {
  return useQuery<MarginSummaryValues>({
    queryKey: ["dashboard", "margin-summary-values", params],
    queryFn: () => dashboardService.getMarginSummaryValues(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMarginTrendsMonthlyPerformance = (params?: { firmId?: number }) => {
  return useQuery<MarginTrendMonthlyPerformance[]>({
    queryKey: ["dashboard", "margin-trends-monthly-performance", params],
    queryFn: () => dashboardService.getMarginTrendsMonthlyPerformance(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreditAgingVsMargin = (params?: { firmId?: number }) => {
  return useQuery<CreditAgingVsMargin[]>({
    queryKey: ["dashboard", "credit-aging-vs-margin", params],
    queryFn: () => dashboardService.getCreditAgingVsMargin(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePlanDetails = () => {
  return useQuery<PlanDetails[]>({
    queryKey: ["dashboard", "plan-details"],
    queryFn: () => dashboardService.getPlanDetails(),
    staleTime: 5 * 60 * 1000,
  });
};

